/**
 * Tirar ou escolher uma foto.
 *
 * Usa `<input type="file" accept="image/*" capture>` em vez de `getUserMedia`
 * com prévia ao vivo, e é uma escolha, não uma economia: o input abre a câmera
 * nativa do iPhone, com foco, flash, HDR e o botão gigante que o barbeiro
 * consegue acertar com uma mão suja de talco. Uma prévia feita por nós seria
 * pior em todos esses pontos, e ainda pediria permissão de câmera na primeira
 * vez — que é onde metade das pessoas desiste.
 *
 * `capture` é uma dica, não uma ordem: no computador o mesmo input abre o
 * seletor de arquivos, que é exatamente o que se quer ali.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type ResizeOptions, resizeImage } from '../lib/image.ts';
import { Button } from './ui.tsx';

interface Props {
  label: string;
  /**
   * Chamado com o base64 já reduzido, pronto para o contrato da API.
   *
   * Se devolver uma promessa, o botão fica em carregamento até ela resolver —
   * é o que evita o barbeiro tocar duas vezes achando que não pegou.
   */
  onPicked: (data: string) => unknown;
  options: ResizeOptions;
  /** Câmera frontal para retrato, traseira para o corte. */
  facing?: 'user' | 'environment';
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  busy?: boolean;
}

export function PhotoPicker({
  label,
  onPicked,
  options,
  facing = 'environment',
  variant = 'secondary',
  disabled,
  busy,
}: Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(file: File | undefined) {
    if (!file) return;

    setWorking(true);
    setError(null);

    try {
      const resized = await resizeImage(file, options);
      await onPicked(resized.data);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Não deu para usar esta foto');
    } finally {
      setWorking(false);
      // Sem isto, escolher o mesmo arquivo duas vezes seguidas não dispara
      // evento nenhum e o barbeiro acha que o app travou.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        capture={facing}
        className="hidden"
        onChange={(event) => handle(event.target.files?.[0])}
      />
      <Button
        variant={variant}
        full
        disabled={disabled}
        loading={working || busy}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      {error && <p className="text-[13px] text-danger">{error}</p>}
    </div>
  );
}

/**
 * A moldura redonda que aparece na ficha e na lista.
 *
 * Sem foto, mostra as iniciais — que aqui fazem sentido, ao contrário das da
 * barbearia: entre quinze fichas abertas no mesmo dia, a inicial ajuda a achar
 * a certa, e um ícone genérico repetido quinze vezes não ajuda em nada.
 */
export function Avatar({
  name,
  photoUrl,
  size = 44,
}: {
  name: string;
  photoUrl: string | null;
  size?: number;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        width={size}
        height={size}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-full bg-surface-sunken font-semibold text-ink-soft"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initials}
    </span>
  );
}

/**
 * O `Avatar` que amplia ao toque, para onde a foto aparece sozinha — a ficha e
 * a edição do cliente. Sem foto não há o que ampliar, e as iniciais continuam
 * sendo só desenho, sem fingir que são botão.
 */
export function ZoomableAvatar({
  name,
  photoUrl,
  size,
  detail,
}: {
  name: string;
  photoUrl: string | null;
  size?: number;
  detail?: string;
}) {
  const [open, setOpen] = useState(false);

  if (!photoUrl) return <Avatar name={name} photoUrl={null} size={size} />;

  return (
    <>
      <button
        type="button"
        aria-label={`Ver a foto de ${name}`}
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-full"
      >
        <Avatar name={name} photoUrl={photoUrl} size={size} />
      </button>
      {open && (
        <PhotoViewer
          name={name}
          photoUrl={photoUrl}
          detail={detail}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * A foto em tela cheia, para tirar a dúvida entre dois "João Pedro".
 *
 * Os 36px da lista bastam para reconhecer quem já se conhece de vista, não
 * para desempatar homônimos — e desempatar é o motivo de a foto estar ali. A
 * ação de escolher vem junto, embaixo: quem ampliou para conferir já decidiu
 * ao ver, e fechar para depois tocar na linha seria um passo a mais.
 *
 * Fica acima da folha (`z-[60]`) e vai para o `body` pelo mesmo motivo dela. O
 * Esc é ouvido na captura e não segue adiante: a folha de baixo também fecha
 * com Esc, e uma tecla não pode fechar as duas.
 */
export function PhotoViewer({
  name,
  photoUrl,
  detail,
  onClose,
  action,
}: {
  name: string;
  photoUrl: string;
  detail?: string;
  onClose: () => void;
  action?: { label: string; onClick: () => void; loading?: boolean };
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div className="animate-fade fixed inset-0 z-[60] flex flex-col bg-black/95">
      <button
        type="button"
        aria-label="Fechar foto"
        className="flex min-h-0 flex-1 items-center justify-center px-4 pt-safe"
        onClick={onClose}
      >
        <img
          src={photoUrl}
          alt={`Foto de ${name}`}
          className="max-h-full max-w-full rounded-2xl object-contain"
        />
      </button>
      <div className="pb-safe flex shrink-0 flex-col gap-3 px-4 pt-4">
        <div className="text-center">
          <p className="truncate text-[17px] font-semibold text-white">{name}</p>
          {detail && <p className="text-[13px] text-white/70">{detail}</p>}
        </div>
        <div className="flex gap-2">
          {action && (
            <Button className="flex-1" loading={action.loading} onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          <Button variant="secondary" className={action ? undefined : 'flex-1'} onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
